import React, { forwardRef, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, withRouter } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useQuery, useMutation } from "react-apollo-hooks";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "../../../../Containers/SelectedPeriod/DatePicker.css";
import ko from "date-fns/locale/ko";
import getYear from "date-fns/getYear";
import getMonth from "date-fns/getMonth";
import {
  MANAGER_DONATION_DETAIL_QUERY,
  MANAGER_DONATION_EDIT_QUERY,
} from "../../Queries";
import { Panel, PanelHeader, PanelBody } from "../../../../Components/Panel/Panel";
import { graphqlUri } from "../../../../Config/Env";

export default withRouter(({ match, location, history }) => {
  const donationId = match.params.donationId;

  const { data, loading } = useQuery(MANAGER_DONATION_DETAIL_QUERY, {
    variables: { id: donationId },
    fetchPolicy: "network-only",
  });

  if (loading || !data || !data.ManagerDonationDetail) {
    return (
      <div className="panel-loader">
        <span className="spinner-small"></span>
      </div>
    );
  }

  return (
    <DonationEditForm
      donation={data.ManagerDonationDetail}
      donationId={donationId}
      location={location}
      history={history}
    />
  );
});

const DonationEditForm = ({ donation, donationId, location, history }) => {
  const [startDate, setStartDate] = useState(donation.startDate || new Date());
  const [endDate, setEndDate] = useState(donation.endDate || new Date());

  const [donationEditMutation] = useMutation(MANAGER_DONATION_EDIT_QUERY, {
    refetchQueries: () => [
      { query: MANAGER_DONATION_DETAIL_QUERY, variables: { id: donationId } },
    ],
  });

  const { register, handleSubmit, errors, watch } = useForm({
    defaultValues: {
      title: donation.title,
      contents: donation.contents,
      planner: donation.planner,
      plannerTel: donation.plannerTel,
      plannerUseSNS: donation.plannerUseSNS,
      plannerSNS: donation.plannerSNS,
      goalAmount: donation.goalAmount,
    },
  });

  const [donationCoverImg, setDonationCoverImg] = useState(
    donation.coverImg
      ? graphqlUri + donation.coverImg
      : "/assets/img/gallery/gallery-default.jpg"
  );
  const [donationDetailImg, setDonationDetailImg] = useState(
    donation.detailImg
      ? graphqlUri + donation.detailImg
      : "/assets/img/gallery/gallery-default.jpg"
  );
  const handleDonationCoverImg = (e) => {
    e.preventDefault();
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => setDonationCoverImg(reader.result);
  };
  const handleDonationDetailImg = (e) => {
    e.preventDefault();
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => setDonationDetailImg(reader.result);
  };

  const onSubmit = async (form) => {
    try {
      let variables = {
        id: donationId,
        title: form.title,
        contents: form.contents,
        planner: form.planner,
        plannerTel: form.plannerTel,
        plannerUseSNS: form.plannerUseSNS,
        plannerSNS: form.plannerUseSNS === "S" ? "-" : form.plannerSNS,
        goalAmount: Number(form.goalAmount),
        startDate,
        endDate,
      };
      if (form.coverImg[0]) {
        variables = { ...variables, coverImg: form.coverImg[0] };
      }
      if (form.detailImg[0]) {
        variables = { ...variables, detailImg: form.detailImg[0] };
      }

      const {
        data: { ManagerDonationEdit },
      } = await donationEditMutation({ variables });
      if (ManagerDonationEdit) {
        toast.success("수정 되었습니다.");
        history.push(`/manager/donations${location.search}`);
      } else {
        toast.error("수정에 실패했습니다.");
      }
    } catch (e) {
      const msg = e.toString().replace("Error: GraphQL error:", "").trim();
      toast.error(msg || "에러가 발생했습니다. 다시 시도해 주세요.");
    }
  };

  const CustomInput = forwardRef(({ value, onClick }, ref) => (
    <button type="button" className="btn btn-white" onClick={onClick} ref={ref}>
      {value}
      <i className="fa fa-calendar fa-fw text-black-transparent-5 m-l-5"></i>
    </button>
  ));

  const renderHeader = ({
    date,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
    decreaseMonth,
    increaseMonth,
  }) => (
    <div className="custom-react-datepicker__select-wrapper">
      <button
        type="button"
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
      >
        <i className="fa fa-angle-left fa-lg"></i>
      </button>
      <div className="month-day">
        {getYear(date)}년 {getMonth(date) + 1}월
      </div>
      <button
        type="button"
        onClick={increaseMonth}
        disabled={nextMonthButtonDisabled}
      >
        <i className="fa fa-angle-right fa-lg"></i>
      </button>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>기부 수정 | 한밭페이</title>
      </Helmet>
      <form className="margin-bottom-0" onSubmit={handleSubmit(onSubmit)}>
        <div>
          <ol className="breadcrumb float-xl-right">
            <li className="breadcrumb-item">
              <Link to={`/manager/donations${location.search}`}>담당 기부</Link>
            </li>
            <li className="breadcrumb-item active">기부 수정</li>
          </ol>
          <h1 className="page-header">기부 수정</h1>

          <div className="row m-b-15">
            <div className="col-xs-6 text-left">
              <Link
                to={`/manager/donations${location.search}`}
                className="btn btn-white"
              >
                <i className="fa fa-list m-r-5"></i>목록
              </Link>
            </div>
            <div className="col-xs-6 text-right">
              <button type="submit" className="btn btn-white text-success">
                <i className="fa fa-check m-r-5"></i>수정
              </button>
            </div>
          </div>

          <div className="row">
            <div className="col-md-12 col-sm-12">
              <Panel>
                <PanelHeader noButton={true}>수정</PanelHeader>
                <PanelBody className="panel-form">
                  <div className="form-horizontal form-bordered">
                    <div className="form-group row">
                      <label className="col-form-label col-md-3">
                        프로젝트 기간
                      </label>
                      <div className="col-md-9">
                        <div className="input-group">
                          <DatePicker
                            customInput={<CustomInput />}
                            locale={ko}
                            name="startDate"
                            dateFormat="yyyy-MM-dd"
                            selected={Date.parse(startDate)}
                            onChange={(date) => setStartDate(date)}
                            renderCustomHeader={renderHeader}
                          />
                          <span className="custom-react-datepicker__split-span">
                            ~
                          </span>
                          <DatePicker
                            customInput={<CustomInput />}
                            locale={ko}
                            name="endDate"
                            dateFormat="yyyy-MM-dd"
                            selected={Date.parse(endDate)}
                            onChange={(date) => setEndDate(date)}
                            renderCustomHeader={renderHeader}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="form-group row">
                      <label className="col-form-label col-md-3">
                        프로젝트 관리자
                      </label>
                      <div className="col-md-9">
                        <input
                          name="planner"
                          type="text"
                          className="form-control m-b-5"
                          placeholder="프로젝트 관리자 입력"
                          ref={register({
                            required: "프로젝트 관리자를 입력해주세요.",
                          })}
                        />
                        {errors.planner && (
                          <span>{errors.planner.message}</span>
                        )}
                      </div>
                    </div>
                    <div className="form-group row">
                      <label className="col-form-label col-md-3">
                        프로젝트 관리자 연락처
                      </label>
                      <div className="col-md-9">
                        <input
                          name="plannerTel"
                          type="text"
                          className="form-control m-b-5"
                          placeholder="프로젝트 관리자 연락처 입력"
                          ref={register({
                            required: "프로젝트 관리자 연락처를 입력해주세요.",
                          })}
                        />
                        {errors.plannerTel && (
                          <span>{errors.plannerTel.message}</span>
                        )}
                      </div>
                    </div>
                    <div className="form-group row">
                      <label className="col-form-label col-md-3">
                        SNS 사용 여부
                      </label>
                      <div className="col-md-9">
                        <div className="radio radio-css radio-inline">
                          <input
                            type="radio"
                            id="snsUseC"
                            name="plannerUseSNS"
                            className="form-control"
                            value="C"
                            ref={register({ required: "선택해주세요." })}
                          />
                          <label htmlFor="snsUseC">사용</label>
                        </div>
                        <div className="radio radio-css radio-inline">
                          <input
                            type="radio"
                            id="snsUseS"
                            name="plannerUseSNS"
                            className="form-control"
                            value="S"
                            ref={register({ required: "선택해주세요." })}
                          />
                          <label htmlFor="snsUseS">미사용</label>
                        </div>
                        {errors.plannerUseSNS && (
                          <div>
                            <span>{errors.plannerUseSNS.message}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    {watch("plannerUseSNS") === "C" && (
                      <div className="form-group row">
                        <label className="col-form-label col-md-3">
                          프로젝트 관리자 SNS
                        </label>
                        <div className="col-md-9">
                          <input
                            name="plannerSNS"
                            type="text"
                            className="form-control m-b-5"
                            placeholder="프로젝트 관리자 SNS 입력"
                            ref={register({
                              validate: (value) =>
                                watch("plannerUseSNS") === "C" && value === ""
                                  ? "프로젝트 관리자 SNS를 입력해주세요."
                                  : undefined,
                            })}
                          />
                          {errors.plannerSNS && (
                            <span>{errors.plannerSNS.message}</span>
                          )}
                        </div>
                      </div>
                    )}
                    <div className="form-group row">
                      <label className="col-form-label col-md-3">
                        프로젝트 목표 금액
                      </label>
                      <div className="col-md-9">
                        <input
                          name="goalAmount"
                          type="text"
                          className="form-control m-b-5"
                          placeholder="프로젝트 목표 금액 입력"
                          ref={register({
                            required: "프로젝트 목표 금액을 입력해주세요.",
                          })}
                        />
                        {errors.goalAmount && (
                          <span>{errors.goalAmount.message}</span>
                        )}
                      </div>
                    </div>
                    <div className="form-group row">
                      <label className="col-form-label col-md-3">제목</label>
                      <div className="col-md-9">
                        <input
                          type="text"
                          name="title"
                          className="form-control m-b-5"
                          placeholder="제목 입력"
                          ref={register({ required: "제목을 입력해주세요." })}
                        />
                        {errors.title && <span>{errors.title.message}</span>}
                      </div>
                    </div>
                    <div className="form-group row">
                      <div className="col-xs-12">
                        <textarea
                          name="contents"
                          className="form-control m-b-5"
                          placeholder="내용 입력"
                          ref={register({ required: "내용을 입력해주세요." })}
                          rows="10"
                        ></textarea>
                        {errors.contents && (
                          <span>{errors.contents.message}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </PanelBody>
              </Panel>
            </div>

            <div className="col-md-3 col-sm-12">
              <Panel>
                <PanelHeader noButton={true}>커버 이미지</PanelHeader>
                <PanelBody>
                  <div className="bg-gray-transparent-1 rounded">
                    <div
                      className="coverImg"
                      style={{ backgroundImage: `url(${donationCoverImg})` }}
                    >
                      <img
                        src={donationCoverImg}
                        alt="이미지 업로드"
                        className="sr-only"
                      />
                    </div>
                  </div>
                  <div className="m-t-10">
                    <input
                      type="file"
                      id="coverImg"
                      name="coverImg"
                      className="hide"
                      onChange={handleDonationCoverImg}
                      ref={register({})}
                    />
                    <button
                      type="button"
                      className="btn btn-primary btn-block"
                      onClick={() => document.getElementById("coverImg").click()}
                    >
                      커버 이미지 업로드
                    </button>
                  </div>
                  <div className="alert alert-primary fade show m-t-10 m-b-0">
                    <b>커버이미지 사이즈 :</b> 넓이 1200px X 높이 1024px
                  </div>
                </PanelBody>
              </Panel>
            </div>

            <div className="col-md-3 col-sm-12">
              <Panel>
                <PanelHeader noButton={true}>상세 이미지</PanelHeader>
                <PanelBody>
                  <div className="bg-gray-transparent-1 rounded">
                    <div
                      className="coverImg"
                      style={{ backgroundImage: `url(${donationDetailImg})` }}
                    >
                      <img
                        src={donationDetailImg}
                        alt="이미지 업로드"
                        className="sr-only"
                      />
                    </div>
                  </div>
                  <div className="m-t-10">
                    <input
                      type="file"
                      id="detailImg"
                      name="detailImg"
                      className="hide"
                      onChange={handleDonationDetailImg}
                      ref={register({})}
                    />
                    <button
                      type="button"
                      className="btn btn-primary btn-block"
                      onClick={() =>
                        document.getElementById("detailImg").click()
                      }
                    >
                      상세 이미지 업로드
                    </button>
                  </div>
                  <div className="alert alert-primary fade show m-t-10 m-b-0">
                    <b>이미지 사이즈 :</b> 넓이 1200px X 높이 1200px
                  </div>
                </PanelBody>
              </Panel>
            </div>
          </div>

          <div className="row">
            <div className="col-xs-6 text-left">
              <Link
                to={`/manager/donations${location.search}`}
                className="btn btn-white"
              >
                <i className="fa fa-list m-r-5"></i>목록
              </Link>
            </div>
            <div className="col-xs-6 text-right">
              <button type="submit" className="btn btn-white text-success">
                <i className="fa fa-check m-r-5"></i>수정
              </button>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};
